import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";

const PLACEHOLDER = "https://images.unsplash.com/photo-1522065893269-6fd20f6d7438?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ROUND = "grid place-items-center rounded-full bg-background text-foreground shadow-lg hover:scale-110 transition-transform";

function useSwipe(many, onMove) {
  const start = useRef(null);
  const [dx, setDx] = useState(0);
  const [dir, setDir] = useState(0);
  const handlers = many ? {
    onTouchStart: (e) => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; },
    onTouchMove: (e) => {
      if (!start.current) return;
      const x = e.touches[0].clientX - start.current.x;
      if (Math.abs(x) > Math.abs(e.touches[0].clientY - start.current.y)) setDx(x);
    },
    onTouchEnd: () => {
      if (Math.abs(dx) > 50) { const d = dx < 0 ? 1 : -1; setDir(d); onMove(d); }
      start.current = null; setDx(0);
    },
  } : {};
  return { dx, dir, handlers };
}

function Lightbox({ images, index, alt, onClose, onMove }) {
  const many = images.length > 1;
  const { dx, dir, handlers } = useSwipe(many, onMove);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (many && e.key === "ArrowRight") onMove(1);
      if (many && e.key === "ArrowLeft") onMove(-1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose, onMove, many]);
  return createPortal(
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/55 backdrop-blur-[2px] p-4 sm:p-8 animate-in fade-in duration-200"
      onClick={onClose} role="dialog" aria-modal="true" data-testid="photo-lightbox">
      <div className="relative w-[min(1000px,92vw)] aspect-[4/3] max-h-[82vh] lg:w-[70vw] lg:h-[70vh] lg:aspect-auto lg:max-h-none animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()} data-testid="photo-lightbox-frame">
        <div className="absolute inset-0 overflow-hidden rounded-2xl bg-neutral-900 shadow-2xl touch-pan-y select-none" {...handlers} data-testid="photo-lightbox-swipe">
          <img key={`bg-${index}`} src={fileUrl(images[index])} alt="" aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" />
          <img key={index} src={fileUrl(images[index])} alt={alt}
            className={`relative w-full h-full object-contain animate-in fade-in duration-300 ${dir > 0 ? "slide-in-from-right-10" : dir < 0 ? "slide-in-from-left-10" : ""} ${dx ? "" : "transition-transform"}`}
            style={dx ? { transform: `translateX(${dx}px)` } : undefined} draggable={false}
            data-testid="photo-lightbox-img" />
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar" className={`absolute -top-3 -right-3 w-10 h-10 ${ROUND}`} data-testid="photo-lightbox-close">
          <X className="w-5 h-5" />
        </button>
        {many && <>
          <button type="button" onClick={() => onMove(-1)} aria-label="Foto anterior"
            className={`absolute top-1/2 -translate-y-1/2 left-2 sm:-left-5 w-11 h-11 bg-background/90 ${ROUND}`} data-testid="photo-lightbox-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button type="button" onClick={() => onMove(1)} aria-label="Foto siguiente"
            className={`absolute top-1/2 -translate-y-1/2 right-2 sm:-right-5 w-11 h-11 bg-background/90 ${ROUND}`} data-testid="photo-lightbox-next">
            <ChevronRight className="w-6 h-6" />
          </button>
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-semibold text-white" data-testid="photo-lightbox-counter">
            {index + 1} / {images.length}
          </span>
        </>}
      </div>
    </div>,
    document.body,
  );
}

export function PhotoMosaic({ images, alt = "", className = "" }) {
  const imgs = (images?.length ? images : [PLACEHOLDER]).slice(0, 3);
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const move = useCallback((d) => setOpen((i) => (i + d + imgs.length) % imgs.length), [imgs.length]);
  const cell = (i, extra = "") => (
    <button type="button" key={i} onClick={() => setOpen(i)} className={`relative min-h-0 overflow-hidden cursor-pointer group/cell ${extra}`} data-testid={`mosaic-photo-${i}`}>
      <img src={fileUrl(imgs[i])} alt={i === 0 ? alt : ""} className="w-full h-full object-cover transition-transform duration-500 group-hover/cell:scale-105" />
    </button>
  );
  return (
    <div className={`relative overflow-hidden ${className}`} data-testid="photo-mosaic">
      <div className={`grid h-full gap-1 ${imgs.length > 1 ? "grid-cols-2 grid-rows-2" : ""}`}>
        {cell(0, imgs.length > 1 ? "row-span-2" : "")}
        {imgs.length === 2 && cell(1, "row-span-2")}
        {imgs.length === 3 && <>{cell(1)}{cell(2)}</>}
      </div>
      {open !== null && <Lightbox images={imgs} index={open} alt={alt} onClose={close} onMove={move} />}
    </div>
  );
}
